import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import type { TFunction } from 'i18next'

export const formatDate = (date: number | string | null | undefined): string => {
  if (!date) return 'N/A'
  try {
    let dateObj: Date
    if (typeof date === 'number') {
      dateObj = new Date(date * 1000)
    } else if (typeof date === 'string') {
      dateObj = parseISO(date)
    } else {
      return 'N/A'
    }
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid date:', date)
      return 'N/A'
    }
    return format(dateObj, 'dd/MM/yyyy', { locale: fr })
  } catch (error) {
    console.error('Error formatting date:', date, error)
    return 'N/A'
  }
}

export const formatDateTime = (date: number | string | null | undefined): string => {
  if (!date) return 'N/A'
  try {
    let dateObj: Date
    if (typeof date === 'number') {
      dateObj = new Date(date * 1000)
    } else if (typeof date === 'string') {
      dateObj = parseISO(date)
    } else {
      return 'N/A'
    }
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid datetime:', date)
      return 'N/A'
    }
    return format(dateObj, 'dd/MM/yyyy HH:mm', { locale: fr })
  } catch (error) {
    console.error('Error formatting datetime:', date, error)
    return 'N/A'
  }
}

export const formatCurrency = (amount: number, currency: string = 'EUR'): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

// ─── Invoice ────────────────────────────────────────────────────────────────

const getInvoiceStatusKey = (statut: string | number, paye: number | boolean): string => {
  if (paye === 1 || paye === true) return 'status.invoice.paid'
  // Some adapters (Salesforce, ERPNext, Business Central) normalize status to
  // 'unpaid'/'paid'/'late'/'cancelled'/'draft' instead of Dolibarr's numeric codes.
  if (statut === 'unpaid') return 'status.invoice.validated'
  if (statut === 'paid') return 'status.invoice.paid'
  if (statut === 'late') return 'status.invoice.late'
  const statusNum =
    typeof statut === 'string'
      ? statut === 'draft'
        ? 0
        : statut === 'validated'
          ? 1
          : statut === 'abandoned'
            ? 2
            : statut === 'cancelled'
              ? 3
              : -1
      : statut
  switch (statusNum) {
    case 0:
      return 'status.invoice.draft'
    case 1:
      return 'status.invoice.validated'
    case 2:
      return 'status.invoice.abandoned'
    case 3:
      return 'status.invoice.cancelled'
    default:
      return 'status.common.unknown'
  }
}

export const getInvoiceStatusLabel = (
  statut: string | number,
  paye: number | boolean,
  t: TFunction
): string => t(getInvoiceStatusKey(statut, paye))

export const getInvoiceStatusColor = (statut: string | number, paye: number | boolean): string => {
  if (paye === 1 || paye === true) return 'bg-green-100 text-green-800'
  // Some adapters (Salesforce, ERPNext, Business Central) normalize status to
  // 'unpaid'/'paid'/'late'/'cancelled'/'draft' instead of Dolibarr's numeric codes.
  if (statut === 'unpaid') return 'bg-yellow-100 text-yellow-800'
  if (statut === 'paid') return 'bg-green-100 text-green-800'
  if (statut === 'late') return 'bg-orange-100 text-orange-800'
  const statusNum =
    typeof statut === 'string'
      ? statut === 'draft'
        ? 0
        : statut === 'validated'
          ? 1
          : statut === 'abandoned'
            ? 2
            : statut === 'cancelled'
              ? 3
              : -1
      : statut
  switch (statusNum) {
    case 0:
      return 'bg-gray-100 text-gray-800'
    case 1:
      return 'bg-yellow-100 text-yellow-800'
    case 2:
      return 'bg-red-100 text-red-800'
    case 3:
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// ─── Order ───────────────────────────────────────────────────────────────────

const getOrderStatusKey = (statut: string | number): string => {
  const statusNum =
    typeof statut === 'string'
      ? statut === 'draft'
        ? 0
        : statut === 'validated'
          ? 1
          : statut === 'shipment_in_progress'
            ? 2
            : statut === 'shipped'
              ? 3
              : statut === 'cancelled'
                ? -1
                : -999
      : statut
  switch (statusNum) {
    case 0:
      return 'status.order.draft'
    case 1:
      return 'status.order.validated'
    case 2:
      return 'status.order.shipment_in_progress'
    case 3:
      return 'status.order.shipped'
    case -1:
      return 'status.order.cancelled'
    default:
      return 'status.common.unknown'
  }
}

export const getOrderStatusLabel = (statut: string | number, t: TFunction): string =>
  t(getOrderStatusKey(statut))

export const getOrderStatusColor = (statut: string | number): string => {
  const statusNum =
    typeof statut === 'string'
      ? statut === 'draft'
        ? 0
        : statut === 'validated'
          ? 1
          : statut === 'shipment_in_progress'
            ? 2
            : statut === 'shipped'
              ? 3
              : statut === 'cancelled'
                ? -1
                : -999
      : statut
  switch (statusNum) {
    case 0:
      return 'bg-gray-100 text-gray-800'
    case 1:
      return 'bg-blue-100 text-blue-800'
    case 2:
      return 'bg-yellow-100 text-yellow-800'
    case 3:
      return 'bg-green-100 text-green-800'
    case -1:
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// ─── Shipment ────────────────────────────────────────────────────────────────

const getShipmentStatusKey = (statut: string | number): string => {
  const statusNum =
    typeof statut === 'string'
      ? statut === 'draft'
        ? 0
        : statut === 'validated'
          ? 1
          : statut === 'closed'
            ? 2
            : statut === 'cancelled'
              ? -1
              : -999
      : statut
  switch (statusNum) {
    case 0:
      return 'status.shipment.draft'
    case 1:
      return 'status.shipment.validated'
    case 2:
      return 'status.shipment.closed'
    case -1:
      return 'status.shipment.cancelled'
    default:
      return 'status.common.unknown'
  }
}

export const getShipmentStatusLabel = (statut: string | number, t: TFunction): string =>
  t(getShipmentStatusKey(statut))

export const getShipmentStatusColor = (statut: string | number): string => {
  const statusNum =
    typeof statut === 'string'
      ? statut === 'draft'
        ? 0
        : statut === 'validated'
          ? 1
          : statut === 'closed'
            ? 2
            : statut === 'cancelled'
              ? -1
              : -999
      : statut
  switch (statusNum) {
    case 0:
      return 'bg-gray-100 text-gray-800'
    case 1:
      return 'bg-blue-100 text-blue-800'
    case 2:
      return 'bg-green-100 text-green-800'
    case -1:
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// ─── Ticket ──────────────────────────────────────────────────────────────────

const getTicketStatusKey = (statut: string | number): string => {
  const statusNum =
    typeof statut === 'string'
      ? statut === 'not_read'
        ? 0
        : statut === 'read'
          ? 1
          : statut === 'assigned'
            ? 2
            : statut === 'in_progress'
              ? 3
              : statut === 'waiting'
                ? 4
                : statut === 'need_more_info'
                  ? 5
                  : statut === 'resolved'
                    ? 8
                    : statut === 'closed'
                      ? 9
                      : statut === 'cancelled'
                        ? -1
                        : -999
      : statut
  switch (statusNum) {
    case 0:
      return 'status.ticket.not_read'
    case 1:
      return 'status.ticket.read'
    case 2:
      return 'status.ticket.assigned'
    case 3:
      return 'status.ticket.in_progress'
    case 4:
      return 'status.ticket.waiting'
    case 5:
      return 'status.ticket.need_more_info'
    case 8:
      return 'status.ticket.resolved'
    case 9:
      return 'status.ticket.closed'
    case -1:
      return 'status.ticket.cancelled'
    default:
      return 'status.common.unknown'
  }
}

export const getTicketStatusLabel = (statut: string | number, t: TFunction): string =>
  t(getTicketStatusKey(statut))

export const getTicketStatusColor = (statut: string | number): string => {
  const statusNum =
    typeof statut === 'string'
      ? statut === 'not_read'
        ? 0
        : statut === 'read'
          ? 1
          : statut === 'assigned'
            ? 2
            : statut === 'in_progress'
              ? 3
              : statut === 'waiting'
                ? 4
                : statut === 'need_more_info'
                  ? 5
                  : statut === 'resolved'
                    ? 8
                    : statut === 'closed'
                      ? 9
                      : statut === 'cancelled'
                        ? -1
                        : -999
      : statut
  switch (statusNum) {
    case 0:
      return 'bg-red-100 text-red-800'
    case 1:
      return 'bg-yellow-100 text-yellow-800'
    case 2:
      return 'bg-blue-100 text-blue-800'
    case 3:
      return 'bg-blue-100 text-blue-800'
    case 4:
      return 'bg-orange-100 text-orange-800'
    case 5:
      return 'bg-purple-100 text-purple-800'
    case 8:
      return 'bg-green-100 text-green-800'
    case 9:
      return 'bg-gray-100 text-gray-800'
    case -1:
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export const truncate = (str: string, length: number): string => {
  if (str.length <= length) return str
  return str.substring(0, length) + '...'
}

export const stripHtml = (html: string): string => {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export const sanitizeHtml = (html: string): string => {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '')
}
